import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import i18n from './i18n';
import { getMe } from './api';
import { StorePage } from './pages/StorePage';
import { AllSkinsPage } from './pages/AllSkinsPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { BottomNav } from './components/BottomNav';
import { useApp } from './stores/app';
import { VALORANT_REGIONS, type ValorantRegion } from '@vst/shared';

function Shell() {
  const setMe = useApp((s) => s.setMe);
  const navigate = useNavigate();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    retry: 1,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (meQuery.data) setMe(meQuery.data);
  }, [meQuery.data, setMe]);

  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="font-display uppercase tracking-widest text-valo-muted animate-pulse">
          Loading
        </div>
      </div>
    );
  }

  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div>
          <div className="font-display text-2xl text-valo-red mb-2">Connection error</div>
          <p className="text-sm text-valo-muted">
            {(meQuery.error as Error)?.message ?? 'Cannot connect to the API.'}
          </p>
          <button
            onClick={() => meQuery.refetch()}
            className="mt-4 clip-corners-sm bg-valo-red text-valo-white px-4 py-2 font-display uppercase tracking-widest text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const me = meQuery.data;
  const defaultRegion: ValorantRegion = VALORANT_REGIONS.includes(
    (me.riotRegion ?? 'eu') as ValorantRegion,
  )
    ? (me.riotRegion ?? 'eu') as ValorantRegion
    : 'eu';

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24 min-h-screen">
      <Routes>
        <Route
          path="/"
          element={
            <StorePage
              me={me}
              onWantLink={() => navigate('/login')}
            />
          }
        />
        <Route path="/skins" element={<AllSkinsPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/settings" element={<SettingsPage me={me} />} />
        <Route path="/login" element={<LoginPage defaultRegion={defaultRegion} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <BottomNav />
    </div>
  );
}

export default function App() {
  const [, forceRerender] = useState(0);
  useEffect(() => {
    const handler = () => forceRerender((x) => x + 1);
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
